import { useMemo } from 'react';
import { CONFIG, isEntryEditable, type Entry, type Round } from '@h2h/shared';
import { Lock, Target } from '../../components/ui/icons';
import { StakeLadder } from '../../components/play/cycling-ui';
import type { SeasonRoundResult } from '../../hooks/useQueries';
import type { PlayerRoundDetail } from '../../lib/api';
import { buildStageLadder } from '../play/stages-tab/buildStageLadder';
import { parseStageLabel, stagePhaseTone, stageStatusPill, STAGE_PHASE_PILL } from '../play/stages-tab/helpers';
import { buildCardVMs } from '../../lib/round';
import { cn } from '../../lib/cn';
import { signedPoints, ordinalSuffix } from '../../lib/format';
import { movementVerdictCopy } from '../../lib/divisionExplain';
import { roundDateLabel } from './helpers';
import { buildProfileCyclingEntry } from './buildProfileCyclingEntry';
import { ProfileFootballPickCard } from './ProfileFootballPickCard';
import { ProfileRoundChips } from './ProfileRoundChips';

const CYCLING_PICKS = CONFIG.CYCLING.DUELS_PER_STAGE;

function EmptyState({ icon: Icon, title, body }: { icon: typeof Target; title: string; body: string }) {
  return (
    <div className="flex items-center gap-3 px-[18px] py-8">
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] bg-fill text-ink2">
        <Icon width={18} height={18} strokeWidth={2} />
      </div>
      <div className="min-w-0">
        <div className="text-[14px] font-bold text-ink">{title}</div>
        <div className="mt-0.5 text-[12px] font-medium text-ink2">{body}</div>
      </div>
    </div>
  );
}

/**
 * Unified profile round history — chips, compact header, and hub-style ladder or
 * grouped football picks in one card (replaces separate navigator + recap + card stack).
 */
export function ProfileRoundHistory({
  name,
  loading,
  error,
  detail,
  isSelf,
  seasonResults,
  onSelectRound
}: {
  name: string;
  loading: boolean;
  error?: boolean;
  detail: PlayerRoundDetail | undefined;
  isSelf?: boolean;
  seasonResults: SeasonRoundResult[];
  onSelectRound: (roundId: string) => void;
}) {
  const firstName = name.split(/\s+/)[0];

  if (loading) {
    return (
      <div className="overflow-hidden rounded-[26px] border border-black/[0.08] bg-white p-[18px] shadow-[0_24px_50px_-28px_rgba(20,20,50,0.36)]">
        <div className="h-9 w-48 animate-pulse rounded-xl bg-fill2" />
        <div className="mt-4 h-40 animate-pulse rounded-2xl bg-fill2" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="overflow-hidden rounded-[26px] border border-black/[0.08] bg-white shadow-[0_24px_50px_-28px_rgba(20,20,50,0.36)]">
        <EmptyState icon={Target} title="Couldn't load picks" body="Check your connection and try again." />
      </div>
    );
  }

  const { round, player, sport } = detail;
  const enteredRounds = detail.enteredRounds ?? [];
  const viewingSelf = isSelf || detail.isSelf;

  if (!round) {
    return (
      <div className="overflow-hidden rounded-[26px] border border-black/[0.08] bg-white shadow-[0_24px_50px_-28px_rgba(20,20,50,0.36)]">
        <EmptyState icon={Target} title="No picks to show" body={`${firstName} hasn't played a round here yet.`} />
      </div>
    );
  }

  const orderedEntered = [...enteredRounds].sort((a, b) => a.firstKickoffAt - b.firstKickoffAt);
  const seasonRow = seasonResults.find((r) => r.round.roundId === round.roundId);
  const isCycling = sport === 'CYCLING';
  const anyLive = detail.fixtures.some((f) => f.status === 'LIVE');
  const stageLive = anyLive || round.status === 'LOCKING' || round.status === 'SETTLING';

  return (
    <div className="overflow-hidden rounded-[26px] border border-black/[0.08] bg-white shadow-[0_24px_50px_-28px_rgba(20,20,50,0.36)]">
      {orderedEntered.length > 1 && (
        <ProfileRoundChips
          rounds={orderedEntered}
          selectedId={round.roundId}
          sport={sport}
          onSelect={onSelectRound}
        />
      )}

      <ProfileRoundHeader
        round={round}
        player={player}
        seasonRow={seasonRow}
        anyLive={anyLive}
        sport={sport}
      />

      <ProfileRoundBody
        round={round}
        player={player}
        detail={detail}
        isCycling={isCycling}
        stageLive={stageLive}
        viewingSelf={viewingSelf}
        firstName={firstName}
      />
    </div>
  );
}

function ProfileRoundHeader({
  round,
  player,
  seasonRow,
  anyLive,
  sport
}: {
  round: Round;
  player: PlayerRoundDetail['player'];
  seasonRow?: SeasonRoundResult;
  anyLive: boolean;
  sport: PlayerRoundDetail['sport'];
}) {
  const { heading, route } = parseStageLabel(round.label ?? 'Round');
  const phase = stagePhaseTone(round.status);
  const entryEditable = isEntryEditable(round, [], Date.now());
  const pill =
    sport === 'CYCLING'
      ? stageStatusPill(phase, {
          pickCount: player.duelPicks.length,
          picksRequired: CYCLING_PICKS,
          isCurrentStage: true,
          entryEditable
        })
      : STAGE_PHASE_PILL[phase];
  const date = roundDateLabel(round.firstKickoffAt);

  const rank = seasonRow?.entry.rankInGroup;
  const size = seasonRow?.divisionSize ?? rank ?? 0;
  const movement = seasonRow?.entry.movement ?? 'STAYED';
  const divisionMeta =
    rank != null && size > 0
      ? `${rank}${ordinalSuffix(rank)} of ${size}`
      : null;

  return (
    <div className="border-t border-black/[0.06] px-[18px] py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-[17px] font-extrabold leading-[1.08] tracking-[-0.024em] text-ink">{heading}</h3>
          {route && <p className="mt-0.5 truncate text-[12.5px] font-semibold text-ink2">{route}</p>}
          <p className="mt-1 text-[11.5px] font-medium text-ink3">
            {[date, divisionMeta].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {anyLive ? (
            <span className="rounded-full bg-[rgba(227,168,43,0.18)] px-2.5 py-1 text-[10px] font-bold text-[#B5740A]">
              Live
            </span>
          ) : (
            <span className={cn('rounded-full px-2.5 py-1 text-[10px] font-bold', pill.className)}>{pill.label}</span>
          )}
          {player.hasEntry && (
            <span className="num text-[16px] font-extrabold leading-none text-ink">{signedPoints(player.score)}</span>
          )}
        </div>
      </div>

      {seasonRow && rank != null && size > 0 && (
        <p className="mt-2 text-[11.5px] font-medium text-ink3">
          {movementVerdictCopy({ rank, divisionSize: Math.max(size, rank), movement }).why}
        </p>
      )}
    </div>
  );
}

function ProfileRoundBody({
  round,
  player,
  detail,
  isCycling,
  stageLive,
  viewingSelf,
  firstName
}: {
  round: Round;
  player: PlayerRoundDetail['player'];
  detail: PlayerRoundDetail;
  isCycling: boolean;
  stageLive: boolean;
  viewingSelf: boolean;
  firstName: string;
}) {
  const fixtureMap = useMemo(
    () => new Map(detail.fixtures.map((f) => [f.fixtureId, f])),
    [detail.fixtures]
  );

  if (!player.hasEntry) {
    return (
      <EmptyState
        icon={Target}
        title="No picks this round"
        body={`${firstName} didn't enter ${round.label}.`}
      />
    );
  }

  const revealed = isCycling ? player.duelPicks.length : player.predictions.length;

  if (revealed === 0) {
    return (
      <EmptyState
        icon={Lock}
        title={
          viewingSelf
            ? `${player.lockedCount} picks locked in`
            : `${firstName} is in with ${player.lockedCount} picks`
        }
        body={
          viewingSelf
            ? isCycling
              ? 'Your ladder shows here once the stage starts.'
              : 'Cards appear as each match kicks off.'
            : isCycling
              ? 'Picks reveal when the stage starts.'
              : 'Picks reveal at kickoff.'
        }
      />
    );
  }

  if (isCycling) {
    const entry = buildProfileCyclingEntry(player, round);
    const ladder = entry ? buildStageLadder(entry, detail.fixtures) : null;

    if (ladder && ladder.rungs.length >= CYCLING_PICKS) {
      return (
        <div className="border-t border-black/[0.06]">
          <StakeLadder
            rungs={ladder.rungs}
            weights={ladder.weights}
            readOnly
            rungGrades={ladder.grades}
            stageLive={stageLive}
            embedded
            rankedCount={CYCLING_PICKS}
            total={CYCLING_PICKS}
          />
        </div>
      );
    }
  }

  const now = Date.now();
  const pseudoEntry: Entry = {
    userId: player.userId,
    leagueId: '',
    roundId: round.roundId,
    groupId: '',
    tier: 0,
    predictions: player.predictions,
    submittedAt: 0
  };
  const vms = buildCardVMs(pseudoEntry, fixtureMap, now, round);
  const sortedVms = [...vms].sort((a, b) => {
    const liveRank = (a.status === 'live' ? 0 : 1) - (b.status === 'live' ? 0 : 1);
    return liveRank !== 0 ? liveRank : b.multiplier - a.multiplier;
  });
  const predByFixture = new Map(player.predictions.map((p) => [p.fixtureId, p]));

  return (
    <div className="divide-y divide-black/[0.06] border-t border-black/[0.06]">
      {sortedVms.map((vm) => (
        <ProfileFootballPickCard
          key={vm.fixtureId}
          vm={vm}
          pick={predByFixture.get(vm.fixtureId)!}
          fixture={fixtureMap.get(vm.fixtureId)}
          embedded
        />
      ))}
      {player.lockedCount > 0 && !viewingSelf && (
        <p className="flex items-center justify-center gap-1.5 px-4 py-3 text-[11px] font-semibold text-ink3">
          <Lock width={11} height={11} strokeWidth={2.4} />
          {player.lockedCount} more reveal at kickoff
        </p>
      )}
    </div>
  );
}
